import { useCallback, useState } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useDeleteProductSource } from '@/shared/hooks/api/productSource'
import type { ProductSource } from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { AddProductSourcesDialog } from '@/features/product-catalog/components/AddProductSourcesDialog'
import { ProductSourceListItem } from './ProductSourceListItem'

export function ProductSourcesPanel({
  productId,
  sources,
  uploadingSourceIds,
  onUploadFile,
  onMultiFileUpload,
}: {
  productId: string
  sources: ProductSource[]
  uploadingSourceIds: Set<string>
  onUploadFile: (file: File) => Promise<void>
  onMultiFileUpload?: (files: File[]) => Promise<void>
}) {
  const { t } = useTranslation('productCatalog')
  const [dialogOpen, setDialogOpen] = useState(false)

  const { mutateAsync: deleteSource, isPending: isDeletePending } =
    useDeleteProductSource(productId)

  const handleDelete = useCallback(
    async (sourceId: string) => {
      try {
        await deleteSource(sourceId)
        toast.success(t('sources.source.deleteSuccess'))
      } catch {
        toast.error(t('sources.source.deleteError'))
      }
    },
    [deleteSource, t]
  )

  const hasSources = sources.length > 0

  return (
    <Card>
      <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-2 space-y-0'>
        <CardTitle className='text-base leading-tight'>
          {t('detail.sources')}
        </CardTitle>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='ms-auto gap-1'
          onClick={() => setDialogOpen(true)}
        >
          <Plus className='size-4' />
          {t('sources.panel.addButton')}
        </Button>
      </CardHeader>
      <div className='space-y-2 text-sm text-muted-foreground'>
        {hasSources ? (
          <ul className='space-y-2 pt-1'>
            {sources.map((source) => (
              <li key={source.id} className='px-4'>
                <ProductSourceListItem
                  productId={productId}
                  source={source}
                  isUploading={uploadingSourceIds.has(source.id)}
                  deleteDisabled={isDeletePending}
                  onDelete={() => handleDelete(source.id)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className='px-4 text-center'>{t('sources.panel.addFilesHint')}</p>
        )}
      </div>
      <AddProductSourcesDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onFileSelect={onUploadFile}
        onMultiFileSelect={onMultiFileUpload}
      />
    </Card>
  )
}
